import { useState } from "react";
import { Alert, Button, InputText } from "@/deps/ui-library";
import { usePartyStatement, useReconcileStatement } from "@/api/queries";
import type { Id } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { paisa } from "./standing";

const AMOUNT = /^-?\d+(\.\d{1,2})?$/;

/**
 * Compares the closing balance on a supplier's statement with the payable in the books on that date.
 *
 * A difference doesn't stop it being marked: it can be a delivery not yet billed or a payment in
 * transit, and the record keeps both balances, so what was accepted can be seen later.
 */
export function ReconcileStatementDialog({
  id,
  name,
  onClose,
}: {
  id: Id;
  name: string;
  onClose: () => void;
}) {
  const [date, setDate] = useState(today());
  const [typed, setTyped] = useState("");
  const dated = date !== "" && date <= today();
  const { data: statement } = usePartyStatement(id, "supplier", date, date, dated);
  const reconcile = useReconcileStatement();

  const balance = typed.replaceAll(",", "").trim();
  const entered = AMOUNT.test(balance);
  const book = dated ? statement?.closing : undefined;
  const difference = entered && book !== undefined ? paisa(balance) - paisa(book) : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reconcile statement · ${name}`}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={difference === null || reconcile.isPending}
          onClick={() =>
            reconcile.mutate(
              { id, statementDate: date, statementBalance: balance },
              { onSuccess: onClose },
            )
          }
        >
          {reconcile.isPending ? "Saving" : "Mark reconciled"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {reconcile.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(reconcile.error as Error).message}
          />
        ) : null}
        <p className="text-fg-muted text-sm">
          Compare the supplier's closing balance with the payable in Rigo. Advances are included, as
          on the ledger, so it can differ from Owed.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InputText
            label="Statement date"
            type="date"
            required
            max={today()}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
          <InputText
            label="Closing balance on statement"
            inputMode="decimal"
            required
            placeholder="0.00"
            className="num"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
        <dl className="border-line-soft rounded-md border text-sm">
          <Figure label="Balance in Rigo" value={book === undefined ? "" : formatAmount(book)} />
          <Figure
            label="Balance on supplier statement"
            value={entered ? formatAmount(balance) : ""}
          />
          <Figure
            label="Difference"
            value={difference === null ? "" : formatAmount(Math.abs(difference) / 100)}
          />
        </dl>
        <p
          className={`text-xs ${
            difference === null
              ? "text-fg-muted"
              : difference === 0
                ? "text-good-text"
                : "text-warn-text"
          }`}
        >
          {!dated
            ? "Choose a statement date that isn't in the future."
            : difference === null
              ? "Enter the closing balance shown on the supplier statement."
              : difference === 0
                ? "Balances agree. Marking it reconciled records the statement date on this supplier."
                : "Balances differ. Check unbilled deliveries, payments in transit and debit notes before marking it reconciled."}
        </p>
      </div>
    </Dialog>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-line-soft flex justify-between gap-3 border-b px-3 py-2 last:border-0">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="num text-fg">{value}</dd>
    </div>
  );
}
